import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { registerSW } from 'virtual:pwa-register';
import App from './App.js';
import ScrollToTop from './components/ScrollToTop.js';
import { captureKeyFromUrl } from './storage.js';
import { reloadForUpdate, resumeUpdate } from './update.js';
import './styles.css';

captureKeyFromUrl();
// Adopt the curtain index.html raised if this load is the far side of an update reload (§7).
resumeUpdate();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reads must survive a cold offline launch at the courts (§7).
      gcTime: 1000 * 60 * 60 * 24 * 30,
      refetchOnWindowFocus: true,
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Offline writes are blocked, not queued — iOS Safari has no Background Sync (§7).
      networkMode: 'online',
      retry: 0,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'topspin.query-cache',
});

/*
 * autoUpdate reloads the tab the instant the new worker activates, which is what gets the new
 * version onto phones fastest and is worth keeping. `onNeedReload` takes over that reload
 * rather than cancelling it: same navigation, but announced and covered end to end (§7).
 */
registerSW({ immediate: true, onNeedReload: reloadForUpdate });

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: 1000 * 60 * 60 * 24 * 30 }}
    >
      <BrowserRouter>
        <ScrollToTop />
        <App />
      </BrowserRouter>
    </PersistQueryClientProvider>
  </StrictMode>,
);
